import React, { useState } from "react";
import { CalendarClock, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { AudiencePicker } from "./AudiencePicker";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const emptyItem = () => ({ titulo: "", contenido: "", url: "" });

export const BlogPubBadge = ({ i }) => {
  if (!i.fecha_publicacion) return <span className="text-xs text-[#6B7688]" data-testid={`blog-pub-${i.id}`}>Inmediata</span>;
  const pa = i.publicar_at ? new Date(String(i.publicar_at).endsWith("Z") || String(i.publicar_at).includes("+") ? i.publicar_at : `${i.publicar_at}Z`) : null;
  const futuro = pa && pa > new Date();
  return (
    <span data-testid={`blog-pub-${i.id}`} className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${futuro ? "bg-[#FFF6DB] text-[#7A5B00]" : "bg-[#E2F6EB] text-[#22A860]"}`}>
      {futuro ? "Programado" : "Publicado"} · {fmt(i.fecha_publicacion)} {i.hora_publicacion || ""}
    </span>
  );
};

export const ProgramarBlogs = ({ onDone }) => {
  const [items, setItems] = useState([emptyItem(), emptyItem()]);
  const [f, setF] = useState({ fecha_inicio: today(), hora: "08:00", cada_dias: 7, puntos: 5, notificar: true, programas: ["Todos"], grupos: ["Todos"] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setItems((arr) => arr.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const validos = items.filter((x) => x.titulo.trim());
  const cada = Math.max(1, Number(f.cada_dias) || 1);

  const submit = async () => {
    if (!validos.length) return toast.error("Escribe al menos un título.");
    if (!f.programas.length || !f.grupos.length) return toast.error("Elige al menos un programa y un grupo.");
    setBusy(true);
    try {
      const r = await adminApi.campus.bulkBlogs({ ...f, cada_dias: cada, puntos: Number(f.puntos) || 5, items: validos });
      toast.success(`${r.creados.length} blog(s) programado(s).`);
      setItems([emptyItem(), emptyItem()]);
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  let n = -1;
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6" data-testid="programar-blogs">
      <h3 className="font-extrabold text-[#0A1C30] flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#E9A400]" /> Programar varios blogs</h3>
      <p className="text-xs text-[#6B7688] mt-1">Sube varios artículos a la vez y se publican solos: uno cada cierto número de días, a la hora que elijas, sin límite de fechas.</p>
      <div className="mt-5 space-y-3">
        {items.map((it, i) => {
          if (it.titulo.trim()) n += 1;
          const fecha = it.titulo.trim() ? addDays(f.fecha_inicio || today(), n * cada) : null;
          return (
            <div key={i} className="rounded-xl ring-1 ring-black/10 p-3 space-y-2" data-testid={`bulk-blog-${i}`}>
              <div className="flex gap-2 items-center">
                <span className="text-xs font-extrabold text-[#B99B2E] w-6">{i + 1}</span>
                <Input data-testid={`bulk-blog-titulo-${i}`} value={it.titulo} placeholder="Título del artículo" onChange={(e) => setItem(i, "titulo", e.target.value)} />
                {fecha && <span className="text-[11px] font-bold text-[#0A1C30] whitespace-nowrap" data-testid={`bulk-blog-fecha-${i}`}>{fmt(fecha)} · {f.hora}</span>}
                <button data-testid={`bulk-blog-del-${i}`} onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-2 rounded-lg text-[#C0392B] hover:bg-[#FDECEC]"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea data-testid={`bulk-blog-contenido-${i}`} value={it.contenido} onChange={(e) => setItem(i, "contenido", e.target.value)} placeholder="Artículo completo (o deja vacío y usa una liga)" className="w-full min-h-[70px] rounded-lg border border-black/10 p-2.5 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" />
              <Input data-testid={`bulk-blog-url-${i}`} value={it.url} placeholder="Liga opcional (YouTube / Google Drive)" onChange={(e) => setItem(i, "url", e.target.value)} />
            </div>
          );
        })}
        <button data-testid="bulk-blog-add" onClick={() => setItems((a) => [...a, emptyItem()])} className="text-xs font-bold text-[#B99B2E] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar otro blog</button>
      </div>
      <div className="mt-5 grid sm:grid-cols-4 gap-3">
        <div><Label className="mb-1 block font-bold text-xs">Primer blog se publica el</Label><Input data-testid="bulk-blog-inicio" type="date" value={f.fecha_inicio} onChange={(e) => set("fecha_inicio", e.target.value)} /></div>
        <div><Label className="mb-1 block font-bold text-xs">Hora (CDMX)</Label><Input data-testid="bulk-blog-hora" type="time" value={f.hora} onChange={(e) => set("hora", e.target.value)} /></div>
        <div><Label className="mb-1 block font-bold text-xs">Uno cada (días)</Label><Input data-testid="bulk-blog-cada" type="number" min="1" value={f.cada_dias} onChange={(e) => set("cada_dias", e.target.value)} /></div>
        <div><Label className="mb-1 block font-bold text-xs">Puntos al validar</Label><Input data-testid="bulk-blog-puntos" type="number" value={f.puntos} onChange={(e) => set("puntos", e.target.value)} /></div>
      </div>
      <div className="mt-5"><AudiencePicker testid="bulk-blog-aud" programas={f.programas} grupos={f.grupos} onChange={(v) => setF((p) => ({ ...p, ...v }))} /></div>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch data-testid="bulk-blog-notificar" checked={f.notificar} onCheckedChange={(v) => set("notificar", v)} /> Avisar con notificación al publicarse cada uno</label>
        <button data-testid="bulk-blog-submit" disabled={busy || !validos.length} onClick={submit} className="ml-auto inline-flex items-center gap-2 rounded-xl bg-navy text-white font-bold px-5 py-2.5 text-sm disabled:opacity-40">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Programar {validos.length} blog(s)
        </button>
      </div>
    </div>
  );
};
